import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { ButtonActions } from 'Components/UI';
import { Header } from 'Components/Common';
import { useTranslation, Trans } from 'react-i18next';
import { useAtom } from 'jotai';
import {
  ticketAtom,
  amountToPayAtom,
  tipsAtom,
  goToPayAtom,
  stepPaymentTicketAtom,
  activePersonalizedTipsAtom,
} from 'Atoms/Ticket';
import { staticDataAtom } from 'Atoms/Common';

import { formattingDigitCurrency } from 'utils';

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.containerGrid,
    a: { borderRadius: '33px' },
  },
  title: {
    ...theme.title,
    textAlign: 'center',
    margin: '1rem 0 .5rem 0',
    fontSize: '17px',
  },
  button: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    color: '#fff',
    width: '42px',
    minWidth: '42px',
    height: '42px',
    fontFamily: 'FuturaStdBold',
    fontSize: '18px',
    margin: '0 8px',
    borderRadius: '5px',
    padding: '5px',
    '&:focus': {
      backgroundColor: '#fff',
      color: theme.palette.primary.main,
    },
  },
  link: {
    margin: '1rem 0',
    textDecoration: 'none',
    background: 'none',
    '-webkit-tap-highlight-color': 'transparent',
    '&:focus, &:hover, &:visited, &:link, &:active': {
      textDecoration: 'none',
      backgroundColor: 'none',
    },
  },
}));

const Tips = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [ticket] = useAtom(ticketAtom);
  const { id, order } = ticket;
  const [staticData] = useAtom(staticDataAtom);
  const [amountToPay] = useAtom(amountToPayAtom);
  const [selectedTips, setSelectedTips] = useState(null);
  const [, setActivePersonalizedTips] = useAtom(activePersonalizedTipsAtom);
  const [amount, setAmount] = useState(0);
  const [tips, setTips] = useAtom(tipsAtom);
  const [, setStepPaymentTicket] = useAtom(stepPaymentTicketAtom);
  const [, setGoToPay] = useAtom(goToPayAtom);
  const { countryCode, labelCode } = staticData?.currency;
  const [disabledHover, setDisabledHover] = useState(true);
  useEffect(() => {
    setTips(amount);
  }, [amount]);

  const delayRedirect = (e, to, action) => {
    e.preventDefault();
    if (action === 'pay') {
      setStepPaymentTicket('checkout');
      setGoToPay(true);
    } else {
      setActivePersonalizedTips(true);
    }
    setTimeout(() => history.push(to), 500);
  };

  const userLabel = order?.user ? order.user : t('my_waiter');
  const defaultTips = ticket?.tips?.option?.length ? ticket.tips.option : staticData?.tips;

  return (
    <>
      <Header
        title={t('my_ticket')}
        actions={{
          prev: {
            icon: 'return',
            label: t('go_back'),
            path: `/ticket/:${id}`,
          },
        }}
      ></Header>
      <Grid container justifyContent="center" className={classes.root}>
        <Grid item className={classes.title}>
          <Trans i18nKey="add_tips" name="hello">
            Voulez-vous rajouter un pourboire pour
            {{ userLabel }} ?
          </Trans>
        </Grid>
        <Grid item xs={11}>
          {amountToPay > 0 && (
            <div
              onClick={(e) => {
                setSelectedTips('noTips');
                setAmount(0);
                setDisabledHover(false);
                delayRedirect(e, `/ticket/:${id}/pay/form`, 'pay');
              }}
            >
              <ButtonActions
                label={t('no_tips')}
                styles={{
                  background: `${
                    selectedTips === 'noTips'
                      ? 'backgroundBlue'
                      : 'backgroundWhite'
                  }`,
                  label: selectedTips === 'noTips' ? 'labelWhite' : 'labelDark',
                  radius: 'radius33',
                  hover: disabledHover,
                }}
                color="default"
              />
            </div>
          )}

          {defaultTips.map((option) => {
            const formatAmount = formattingDigitCurrency(
              option,
              countryCode,
              labelCode,
            );
            const selectedOption = `tips-${option}`;
            
            return (
              <div
                key={'k-'+selectedOption}
                onClick={(e) => {
                  setSelectedTips(selectedOption);
                  setAmount(option);
                  setDisabledHover(false);
                  delayRedirect(e, `/ticket/:${id}/pay/form`, 'pay');
                }}
              >
                <ButtonActions
                  label={t(formatAmount)}
                  styles={{
                    background: `${
                      selectedTips === selectedOption
                        ? 'backgroundBlue'
                        : 'backgroundWhite'
                    }`,
                    label:
                      selectedTips === selectedOption
                        ? 'labelWhite'
                        : 'labelDark',
                    radius: 'radius33',
                    hover: disabledHover,
                  }}
                  color="default"
                />
              </div>
            );
          })}
          <div
            onClick={(e) => {
              setDisabledHover(false);
              delayRedirect(
                e,
                `/ticket/:${id}/share/calculator`,
                'personalized',
              );
            }}
          >
            <ButtonActions
              label={t('tips_personalized')}
              styles={{
                background: `${
                  selectedTips === 'personalized'
                    ? 'backgroundBlue'
                    : 'backgroundWhite'
                }`,
                label:
                  selectedTips === 'personalized' ? 'labelWhite' : 'labelDark',
                radius: 'radius33',
                hover: disabledHover,
              }}
              color="default"
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
};

Tips.propTypes = {};

export default Tips;
